/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ssoProviders.js
 * ---------------
 * Client-side SSO provider metadata map and label/icon resolution helper.
 *
 * Contract (Phase 04.1 Plan 03, confirmed via 04.1-03-SUMMARY.md):
 *  - SSO_PROVIDERS maps provider_key strings to { label, icon } objects.
 *  - getProviderMeta(providerKey) implements the D-13 fallback: recognised keys
 *    return their configured metadata; unrecognised keys (including empty string)
 *    fall back to a capitalised key + generic icon — never throws.
 *
 * This file is authored fresh on feature/phase-04.1-sso-error-handling and contains
 * the same logic as the equivalent file produced by Plan 03 (feature/phase-04.1-
 * sso-buttons), reconstructed from Plan 03's SUMMARY.md contract description.
 */

/**
 * Provider metadata map.
 * Keys here get their real brand mark; anything missing falls back to the
 * generic icon in getProviderMeta(), which is why "apple" showed a door-and-arrow
 * glyph until it was seeded below.
 */
const SSO_PROVIDERS = {
  google: { label: "Google", icon: "bi-google" },
  apple: { label: "Apple", icon: "bi-apple" },
};

/**
 * Resolve provider display metadata for a given provider_key.
 *
 * @param {string} providerKey - The provider key returned by GET /arcee/sso/providers/
 * @returns {{ label: string, icon: string }} Display metadata — never throws.
 */
export function getProviderMeta(providerKey) {
  if (providerKey && SSO_PROVIDERS[providerKey]) {
    return SSO_PROVIDERS[providerKey];
  }
  // D-13 fallback: capitalise the key as the label, use generic icon
  const label = providerKey
    ? providerKey.charAt(0).toUpperCase() + providerKey.slice(1)
    : providerKey;
  return { label, icon: "bi-box-arrow-in-right" };
}

// ── Cached provider list ──────────────────────────────────────────────────────
//
// SignInForm and SignUpForm each ran their own uncached effect against
// GET /arcee/sso/providers/. The two never mount together, so this was not a
// duplicate within one screen — but it re-fetched on every visit to either
// screen, and StrictMode's double-invoked effects made each of those two
// requests in development.
//
// The list is deployment configuration. It does not change between two renders
// of a sign-in form, so one answer is cached for the life of the tab and both
// forms share it. A failure is NOT cached: the forms fail open (no SSO buttons)
// and the next mount is free to try again.
let _providersPromise = null;

/**
 * The configured SSO providers, fetched at most once per tab.
 *
 * Resolves to an array — empty on any failure, which is what both callers
 * already treated as "don't show SSO buttons".
 *
 * @param {import('../api/APIHelper').default} apiHelper - caller's helper instance
 * @returns {Promise<Array>}
 */
export function loadSsoProviders(apiHelper) {
  if (_providersPromise) return _providersPromise;

  _providersPromise = (async () => {
    try {
      const resp = await apiHelper.apiCall('/arcee/sso/providers/', { method: 'GET' });
      if (!resp.ok) throw new Error(`sso providers: ${resp.status}`);
      const data = await resp.json();
      return Array.isArray(data) ? data : [];
    } catch (err) {
      // Nothing cached on failure — a network blip must not disable SSO for the
      // rest of the session.
      _providersPromise = null;
      return [];
    }
  })();

  return _providersPromise;
}

/** Test seam: forget the cached provider list. */
export function clearSsoProvidersCache() {
  _providersPromise = null;
}

export default SSO_PROVIDERS;
